<template lang="pug">
.briq-data-table-wrapper
  table.briq-data-table
    thead
      tr
        th.bdt-col(v-for="header in data.headers")
          button(@click="sort(header)" v-if="isSortable(header)")
            span.bold {{ header.label }}
            span.icon-wrapper(:class="iconWrapperClasses(header)")
              BriqIcon(name="icon-input-arrow-down-icon")
          span.bold.header-padding(v-else) {{ header.label }}
        th.actions-col(v-if="showActions") Actions
    .table-message(v-if="!loading && noItems") No data available in table
    tbody(data-test-id="data-table-body")
      tr(
        v-for="(item, i) in sortedItems" :key="i"
        @click="event => $emit('click', {item, event})"
        @contextmenu.prevent="handleRightClick($event, item)"
        :class="{ ['cursor-pointer']: rowsAreClickable }"
        :data-test-id="`tr-${i}`"
        )
        td(v-for="header in data.headers")
          slot(v-if="header.customField" :name="header.field" :item="item")
            div(v-html="header.customField(item, item[header.field])")
          slot(v-else :name="header.field" :item="item") {{ item[header.field] }}
        td(v-if="showActions")
          .action-buttons
            button.action-button(
              v-if="itemCanEmitEdit(item)"
              :data-test-id="`data-table-edit-button-${i}`"
              @click.stop="event => $emit('edit', {item, event})"
              )
              BriqIcon(name="icon-input-edit-icon")
            button.action-button(
              v-if="itemCanEmitDelete(item)"
              :data-test-id="`data-table-delete-button-${i}`"
              @click.stop="$emit('delete', item)"
              )
              BriqIcon(name="icon-input-trash-icon")
  .spinner-wrapper(v-if="loading")
    Spinner
</template>

<script>
import { descend, ascend, sort, prop } from 'ramda'
import Spinner from '@v/components/Spinner.vue'

export default {
  name: 'BriqDataTable',
  components: { Spinner },
  props: {
    data: {
      type: Object,
      required: true,
      validator: obj => Array.isArray(obj.items) && !!obj.headers,
    },
    filter: Function,
    allowDelete: Boolean,
    allowDeleteForItem: Function,
    allowEdit: Boolean,
    allowEditForItem: Function,
    loading: Boolean,
    initialSort: Object,
    rowsAreClickable: { type: Boolean, default: true },
  },
  data() {
    return {
      sortDescend: false,
      sortField: null,
    }
  },
  computed: {
    noItems() {
      return !this.data.items || this.data.items.length === 0
    },
    filteredItems() {
      return this.filter ? this.data.items.filter(this.filter) : this.data.items
    },
    sortedItems() {
      const sorter = this.sortDescend ? descend(prop(this.sortField)) : ascend(prop(this.sortField))
      return this.sortField ? sort(sorter, this.filteredItems) : this.filteredItems
    },
    showActions() {
      return (
        this.allowEdit ||
        this.allowDelete ||
        typeof this.allowDeleteForItem === 'function' ||
        typeof this.allowEditForItem === 'function'
      )
    },
  },
  methods: {
    itemCanEmitEdit(item) {
      if (this.allowEditForItem) return this.allowEditForItem(item)
      return this.allowEdit
    },
    itemCanEmitDelete(item) {
      if (this.allowDeleteForItem) return this.allowDeleteForItem(item)
      return this.allowDelete
    },
    isSortable(header) {
      if (header.sortable === false) return false
      return true
    },
    iconWrapperClasses(header) {
      const selected = this.sortField === (header.sortField || header.field)
      const invert = selected ? !this.sortDescend : true
      return { invert, selected }
    },
    sort(header) {
      let isDescending = false
      if ((header.sortField || header.field) === this.sortField) isDescending = !this.sortDescend
      this.sortDescend = isDescending
      this.sortField = header.sortField || header.field
      if (header.onSort) header.onSort(header, isDescending ? 'desc' : 'asc')
    },
    handleRightClick(e, item) {
      this.$emit('rightClick', e, item)
    },
  },
  mounted() {
    if (this.initialSort) {
      this.sortDescend = this.initialSort.sort === 'desc'
      this.sortField = this.initialSort.sortField
    }
  },
}
</script>

<style scoped lang="stylus">
.briq-data-table-wrapper
  position relative

.spinner-wrapper
  position absolute
  top 0
  left 0
  right 0
  bottom 0
  display flex
  justify-content center
  align-items center
  background: rgba(0, 0, 0, 0.1);
  z-index 10

.briq-data-table
  width 100%
  border-collapse collapse
  border solid 1px #ececec
  box-shadow 0 1px 5px 0 rgba(0,0,0,0.1)
  height fit-content
  font-size 16px
  margin-bottom 40px

  .header-padding 
    padding: 4px 12px;

  th, td
    border 1px solid #ececec
    border-collapse collapse

  td, .table-message
    padding 4px 12px

  th
    padding 0

  th>button
    width 100%
    height 100%
    padding 4px 12px
    display flex
    justify-content space-between
    align-items center
    opacity 0.8
    transition opacity 0.2s ease
    border-radius 0

    &:hover
      opacity 1

      .icon-wrapper
        opacity 0.5

    .icon-wrapper
      display flex
      align-items center
      opacity 0
      transition opacity 0.2s ease
      transition transform 0.2s ease

      &.selected
        opacity 1

  tr
    background white

  tbody
    tr:nth-of-type(odd)
      background #f1f3f6
      padding 8px 16px

    tr:hover
      background #EAEAEA

.invert
  transform rotate(180deg)

th.actions-col
  width 0.1%
  white-space nowrap
  padding 4px 12px

.action-buttons
  display flex
  align-items center
  justify-content center
  width 100%

  .action-button
    display flex
    align-items center
    margin 0 12px
    opacity 0.7
    transition opacity 0.2s ease
    &:hover
      opacity 1
</style>
